import React, { useState } from 'react';
import { PauseCircle, PlayCircle, XCircle, Clock, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const PausedOverlay = ({
    task,
    onResume,
    onAbandon,
    duration,
    accumulatedDuration
}) => {
    const [isAbandoning, setIsAbandoning] = useState(false);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    // Calculate total time in minutes
    const totalMinutes = Math.floor((accumulatedDuration) / 60000);

    const handleAbandon = async () => {
        if (!isAbandoning) {
            setIsAbandoning(true);
            return;
        }

        try {
            setLoading(true);
            await onAbandon();
        } catch (error) {
            console.error('Error abandoning task:', error);
            toast.error('Failed to abandon task');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-gray-900/95 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-300">
            <div className="max-w-lg w-full bg-white rounded-2xl shadow-2xl overflow-hidden text-center">
                <div className="p-8">
                    <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <PauseCircle className="w-10 h-10 text-amber-600" />
                    </div>

                    <h2 className="text-3xl font-bold text-gray-900 mb-2">Task Paused</h2>
                    <p className="text-gray-500 mb-8">
                        Your progress has been saved.
                        {totalMinutes > 0 && <span className="block mt-2 font-medium text-amber-600">Active time: {totalMinutes}m</span>}
                    </p>

                    <div className="space-y-4">
                        <button
                            onClick={onResume}
                            className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-primary hover:bg-primary-hover text-white rounded-xl text-lg font-semibold transition-all hover:scale-[1.02] shadow-lg shadow-primary/30"
                        >
                            <PlayCircle className="w-6 h-6" />
                            Resume Task
                        </button>

                        {!isAbandoning ? (
                            <button
                                onClick={() => setIsAbandoning(true)}
                                className="w-full px-6 py-3 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-xl font-medium transition-colors"
                            >
                                Abandon Task
                            </button>
                        ) : (
                            <div className="bg-red-50 rounded-xl p-4 border border-red-100 animate-in slide-in-from-bottom-2">
                                <div className="flex items-center gap-2 justify-center text-red-800 font-semibold mb-2">
                                    <AlertTriangle className="w-5 h-5" />
                                    Are you sure?
                                </div>
                                <p className="text-sm text-red-600 mb-4">
                                    This will delete all progress and reset the task to "Not Started".
                                </p>
                                <div className="flex gap-3">
                                    <button
                                        onClick={() => setIsAbandoning(false)}
                                        className="flex-1 px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleAbandon}
                                        disabled={loading}
                                        className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium shadow-sm"
                                    >
                                        {loading ? 'Abandoning...' : 'Yes, Abandon'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="bg-gray-50 px-8 py-4 border-t border-gray-100 text-sm text-gray-500">
                    <p>Press <strong>Resume</strong> to continue exactly where you left off.</p>
                </div>
            </div>
        </div>
    );
};

export default PausedOverlay;
